import { useRef, useState } from 'react'
import { Upload, FileText, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/utils'
import { primaryText, smallTextSize, secondaryText } from '../../styling/stylingHelper'

interface FileDropzoneProps {
  files: File[]
  onFilesChange: (files: File[]) => void
  accept?: string
  multiple?: boolean
  disabled?: boolean
  /** Sub-line under the main prompt, e.g. "PDF, Word, Excel, CSV or image". */
  hint?: string
  /** Compact setup card with a browse affordance instead of the large upload icon. */
  compact?: boolean
  prompt?: string
}

/**
 * Reusable click-or-drag file upload area (mirrors the cost-price automation modal's
 * dashed drop zone) with real drag-and-drop and a selected-files list.
 */
export default function FileDropzone({
  files,
  onFilesChange,
  accept,
  multiple = false,
  disabled = false,
  hint,
  compact = false,
  prompt,
}: FileDropzoneProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  const addFiles = (incoming: FileList | null) => {
    if (!incoming || incoming.length === 0) return
    const list = Array.from(incoming)
    onFilesChange(multiple ? [...files, ...list] : [list[0]])
  }

  const removeFile = (index: number) => {
    onFilesChange(files.filter((_, i) => i !== index))
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    if (disabled) return
    addFiles(e.dataTransfer.files)
  }

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !disabled) inputRef.current?.click() }}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setIsDragging(true) }}
        onDragLeave={(e) => { e.preventDefault(); setIsDragging(false) }}
        onDrop={handleDrop}
        className={cn(
          'flex items-center justify-center px-4 border-dashed rounded-xl transition-colors text-center',
          compact ? 'border py-6 bg-backgroundDarker dark:bg-backgroundDarker-dark' : 'border-2 py-10',
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
          isDragging
            ? 'border-darkGreenColor bg-[#F1FAF7] dark:bg-green-950/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-darkGreenColor'
        )}
      >
        <div>
          {!compact && <Upload className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-2" />}
          <span className={cn(primaryText, smallTextSize)}>
            {isDragging ? t('common.fileUpload.dropHere') : (prompt || t('common.fileUpload.clickOrDrag'))}
          </span>
          {hint && (
            <p className={cn(secondaryText, 'text-xs mt-1')}>{hint}</p>
          )}
          {compact && <span className={`mt-3 inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-borderGrey px-3 py-2 text-xs dark:border-borderGrey-dark bg-background dark:bg-background-dark ${primaryText}`}><Upload className="h-4 w-4 text-darkGreenColor dark:text-darkGreenColor-dark" />{t('accountSetup.upload.browse')}</span>}
        </div>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          onChange={(e) => { addFiles(e.target.files); e.target.value = '' }}
        />
      </div>

      {files.length > 0 && (
        <div className="mt-3 space-y-2">
          {files.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/50"
            >
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="h-4 w-4 shrink-0 text-green-600 dark:text-green-400" />
                <span className={cn(primaryText, smallTextSize, 'truncate')}>{file.name}</span>
              </div>
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeFile(index) }}
                  className="shrink-0 text-gray-400 dark:text-textQuaternary-dark hover:text-[#D92D20] dark:hover:text-textRed-dark rounded p-1"
                  aria-label={t('common.fileUpload.remove')}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
